import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { HeartHandshake, Accessibility, UserRound, Users, ShieldAlert, Eye, FileDown } from "lucide-react";
import { API } from "@/lib/api";

const BAR_RADIUS = [4, 4, 0, 0];

function StatCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        </div>
        <div className="text-3xl font-black text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function VulnerabilityBadges({ item }) {
  return (
    <div className="flex flex-wrap gap-1">
      {item.pne_e_responsavel && <Badge className="bg-purple-100 text-purple-800 text-[10px]">PNE resp.</Badge>}
      {item.idoso_e_responsavel && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Idoso resp.</Badge>}
      {item.possui_pcd && !item.pne_e_responsavel && <Badge variant="outline" className="text-purple-700 text-[10px]">PNE</Badge>}
      {item.possui_idoso && !item.idoso_e_responsavel && <Badge variant="outline" className="text-amber-700 text-[10px]">Idoso</Badge>}
      {item.qtd_criancas > 0 && <Badge variant="outline" className="text-blue-700 text-[10px]">{item.qtd_criancas} criança(s)</Badge>}
    </div>
  );
}

function scoreColor(score) {
  if (score >= 100) return "bg-red-600 text-white";
  if (score >= 70) return "bg-orange-500 text-white";
  if (score >= 40) return "bg-yellow-500 text-white";
  return "bg-slate-300 text-slate-800";
}

export default function VulnerabilidadesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.get("/dashboard/vulnerabilidades")
      .then((r) => setData(r.data))
      .catch((e) => { logger.error("Falha ao carregar vulnerabilidades", e); setError(e.response?.data?.detail || "Falha ao carregar dados"); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="p-8 text-center space-y-4" data-testid="vulnerabilidades-error">
      <div className="text-red-600 font-semibold">{error}</div>
      <Button onClick={load} variant="outline">Tentar novamente</Button>
    </div>
  );
  if (!data) return <div className="p-8 text-slate-500">Carregando...</div>;

  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="vulnerabilidades-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-2">
            <ShieldAlert className="w-7 h-7" /> Painel de Vulnerabilidades
          </h1>
          <p className="text-sm text-slate-600">
            Prioridade legal da Faixa 1 do MCMV — famílias com PNE ou idosos como responsável, baixa renda e crianças.
          </p>
        </div>
        <Button
          onClick={() => window.open(`${API}/dashboard/vulnerabilidades/pdf?top=50`, "_blank")}
          className="bg-[#00883A] hover:bg-[#007030]"
          data-testid="btn-export-lista-priorizada"
        >
          <FileDown className="w-4 h-4 mr-2" />Exportar Lista Priorizada (PDF)
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Accessibility} label="Com PNE" value={s.com_pne} sub={`de ${s.total} famílias`} color="bg-purple-600" testid="stat-com-pne" />
        <StatCard icon={HeartHandshake} label="PNE Responsável" value={s.pne_responsavel} sub="prioridade máxima" color="bg-purple-800" testid="stat-pne-resp" />
        <StatCard icon={UserRound} label="Com Idoso" value={s.com_idoso} sub="60+ anos" color="bg-amber-600" testid="stat-com-idoso" />
        <StatCard icon={UserRound} label="Idoso Responsável" value={s.idoso_responsavel} sub="prioridade legal" color="bg-amber-800" testid="stat-idoso-resp" />
        <StatCard icon={Users} label="Ambos (PNE + Idoso)" value={s.ambos} sub="vulnerabilidade dupla" color="bg-rose-700" testid="stat-ambos" />
        <StatCard icon={ShieldAlert} label="Responsável Vulnerável" value={s.responsavel_vulneravel} sub="PNE ou idoso à frente" color="bg-red-700" testid="stat-resp-vuln" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Vulnerabilidade × Faixa de Renda</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.renda_cruzada}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="grupo" fontSize={11} angle={-15} textAnchor="end" height={80} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="valor" fill="#7C3AED" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">Top 30 famílias priorizadas
            <Badge variant="outline" className="text-xs font-normal">Ordenadas por score de vulnerabilidade</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-3 w-16">#</th>
                <th className="p-3">Protocolo / Nome</th>
                <th className="p-3">Bairro</th>
                <th className="p-3">Renda</th>
                <th className="p-3">Vulnerabilidades</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-20">Score</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.prioridades.map((item, idx) => (
                <tr key={item.id} className={idx % 2 ? "bg-slate-50" : ""} data-testid={`priority-row-${idx}`}>
                  <td className="p-3 font-semibold text-slate-400">{idx + 1}</td>
                  <td className="p-3">
                    <div className="font-medium">{item.nome_completo}</div>
                    <div className="text-xs font-mono text-slate-500">{item.protocolo}</div>
                  </td>
                  <td className="p-3">{item.bairro || "-"}</td>
                  <td className="p-3">{formatMoney(item.renda_total)}</td>
                  <td className="p-3"><VulnerabilityBadges item={item} /></td>
                  <td className="p-3"><span className="text-xs">{item.status}</span></td>
                  <td className="p-3">
                    <Badge className={`${scoreColor(item.score)} font-black`}>{item.score}</Badge>
                  </td>
                  <td className="p-3">
                    <Link to={`/admin/inscricoes/${item.id}`} data-testid={`priority-view-${idx}`}>
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
